/**
 * Utility to generate legacy config-reg.json format
 * Used for creating PRs with the old JSON structure
 */

import { ConfigRegistryItem, ConfigRegistryFormData } from '../types/configRegistryTypes';

/**
 * Generate legacy config-reg.json format from a config item
 */
export function generateLegacyConfigJson(
  config: ConfigRegistryItem | ConfigRegistryFormData,
  isFormData: boolean = false
): string {
  let configKey: string;
  let configType: string;
  let configVal: string;
  let configDocumentation: string;
  let configDataType: string;

  if (isFormData) {
    const formData = config as ConfigRegistryFormData;
    configKey = formData.configKey;
    configType = formData.configType;
    configVal = formData.defaultValue || '';
    configDocumentation = formData.configDocumentation || '';
    configDataType = formData.configDataType;
  } else {
    const item = config as ConfigRegistryItem;
    configKey = item.configKey;
    configType = item.configType;
    configVal = item.defaultValue || '';
    configDocumentation = item.configDocumentation || '';
    configDataType = item.configDataType;
  }

  // Escape configVal if it's JSON (check if it starts with { or [)
  let escapedConfigVal = configVal;
  if (configDataType === 'JSON' && configVal) {
    try {
      // Try to parse and re-stringify to ensure it's valid JSON
      const parsed = JSON.parse(configVal);
      escapedConfigVal = JSON.stringify(parsed);
    } catch (e) {
      // If parsing fails, just use the original value
      escapedConfigVal = configVal;
    }
  }

  const legacyFormat = {
    configType: configType,
    configKVs: [
      {
        configKey: configKey,
        configVal: escapedConfigVal,
        configDocumentation: configDocumentation,
        configRegex: '.*',
        configDataType: configDataType,
      },
    ],
  };

  return JSON.stringify(legacyFormat, null, 2);
}

/**
 * Download JSON as a file
 */
export function downloadJsonFile(jsonString: string, filename: string): void {
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}

